import authService from './auth.service';
import {
  EmailThreadTreeResponse,
  EmailAccount,
  EmailAccountCreate,
  EmailAccountUpdate,
  EmailAccountTestResult,
  EmailServiceSettings,
  ResolveEmailsRequest,
  ResolveEmailsResponse,
  EmailAdvancedContext,
  EmailAdvancedPayload,
  EmailNewFilesConfig,
  SendAdvancedResponse,
  BulkEmailPayload,
  BulkEmailResponse,
  EmailJobEntityResponse,
  EmailJobListItem,
  EmailJobListParams,
  EmailJobResponse,
  EmailJobActionResponse,
  EmailEntityMapping,
  EmailEntityMappingCreate,
  EmailEntityMappingUpdate,
  EmailListParams,
  EmailListResponse,
  StoredEmailDetailOut,
} from '../types/email.types';

const API_URL = '/api/v1';

const formatApiError = (payload: unknown, fallback: string): string => {
  const detail = (payload as { detail?: unknown } | null)?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item: unknown) => {
        const e = item as { loc?: unknown[]; msg?: string };
        const loc = Array.isArray(e?.loc)
          ? e.loc.filter((x) => x !== 'body').join('.')
          : '';
        return loc ? `${loc}: ${e?.msg ?? ''}` : (e?.msg ?? '');
      })
      .filter(Boolean);
    return parts.length > 0 ? parts.join('; ') : fallback;
  }
  return fallback;
};

export interface SendAdvancedParams {
  actionCode: string;
  context: EmailAdvancedContext;
  to?: string[];
  subject?: string;
  bodyHtml?: string;
  bodyText?: string;
  accountId?: number;
  fromAddress?: string;
  parentEmailId?: number;
  existingDocumentIds?: number[];
  newFilesConfig?: EmailNewFilesConfig;
  files?: File[];
  isEmailSend?: boolean;
  emailKind?: string;
}

class EmailService {
  async getThreadTree(
    emailId: number,
    params: { page?: number; page_size?: number; order?: 'asc' | 'desc'; only_with_ticket?: boolean } = {}
  ): Promise<EmailThreadTreeResponse> {
    const { page = 1, page_size = 50, order = 'asc', only_with_ticket = false } = params;
    const q = new URLSearchParams();
    q.set('page', String(page));
    q.set('page_size', String(page_size));
    q.set('order', order);
    q.set('only_with_ticket', only_with_ticket ? 'true' : 'false');
    const res = await authService.fetchWithAuth(`${API_URL}/emails/${emailId}/thread-tree?${q.toString()}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить дерево переписки');
    }
    return res.json() as Promise<EmailThreadTreeResponse>;
  }

  async listEmails(params: EmailListParams = {}): Promise<EmailListResponse> {
    const q = new URLSearchParams();
    const set = (key: string, value: unknown) => {
      if (value === undefined || value === null || value === '') return;
      q.set(key, typeof value === 'boolean' ? String(value) : String(value));
    };
    set('search', params.search);
    set('direction', params.direction);
    if (typeof params.is_read === 'boolean') set('is_read', params.is_read);
    set('entity_type', params.entity_type);
    if (typeof params.entity_id === 'number') set('entity_id', params.entity_id);
    if (typeof params.account_id === 'number') set('account_id', params.account_id);
    set('folder_name', params.folder_name);
    set('thread_id', params.thread_id);
    set('message_id', params.message_id);
    if (typeof params.parent_email_id === 'number') set('parent_email_id', params.parent_email_id);
    set('date_from', params.date_from);
    set('date_to', params.date_to);
    if (typeof params.has_attachments === 'boolean') set('has_attachments', params.has_attachments);
    set('from_address', params.from_address);
    set('sort_by', params.sort_by);
    set('order', params.order);
    set('limit', params.limit);
    set('offset', params.offset);
    const qs = q.toString();
    const res = await authService.fetchWithAuth(`${API_URL}/email-service/emails${qs ? `?${qs}` : ''}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить список писем');
    }
    return res.json() as Promise<EmailListResponse>;
  }

  async getEmail(id: number): Promise<StoredEmailDetailOut> {
    const res = await authService.fetchWithAuth(`${API_URL}/email-service/emails/${id}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      if (res.status === 404) {
        throw new Error(err?.detail || `Письмо #${id} не найдено`);
      }
      throw new Error(err?.detail || 'Не удалось загрузить письмо');
    }
    return res.json() as Promise<StoredEmailDetailOut>;
  }

  async listAccounts(): Promise<EmailAccount[]> {
    const res = await authService.fetchWithAuth(`${API_URL}/email-service/accounts`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить email-аккаунты');
    }
    const data = await res.json();
    return (Array.isArray(data) ? data : []) as EmailAccount[];
  }

  async resolveEmails(payload: ResolveEmailsRequest): Promise<ResolveEmailsResponse> {
    const res = await authService.fetchWithAuth(`${API_URL}/email-service/customer-emails/resolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось получить emails клиента');
    }
    return res.json();
  }

  async sendAdvanced(params: SendAdvancedParams): Promise<SendAdvancedResponse> {
    const payload: EmailAdvancedPayload = {
      action_code: params.actionCode,
      context: params.context,
    };
    if (params.to?.length) payload.to = params.to;
    if (params.subject) payload.subject = params.subject;
    if (params.bodyHtml) payload.body_html = params.bodyHtml;
    if (params.bodyText) payload.body_text = params.bodyText;
    if (typeof params.accountId === 'number') payload.account_id = params.accountId;
    if (params.fromAddress) payload.from_address = params.fromAddress;
    if (typeof params.parentEmailId === 'number') payload.parent_email_id = params.parentEmailId;
    if (params.existingDocumentIds?.length) payload.existing_document_ids = params.existingDocumentIds;
    if (params.newFilesConfig) payload.new_files_config = params.newFilesConfig;
    if (params.isEmailSend != null) payload.is_email_send = params.isEmailSend;
    if (params.emailKind) payload.email_kind = params.emailKind;

    const form = new FormData();
    form.append('request', JSON.stringify(payload));
    (params.files || []).forEach((file) => form.append('files', file, file.name));

    const res = await authService.fetchWithAuthForm(`${API_URL}/email-service/send/advanced`, {
      method: 'POST',
      body: form,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || err?.error || 'Не удалось отправить письмо');
    }
    return res.json();
  }

  async listJobs(params: EmailJobListParams = {}): Promise<EmailJobListItem[]> {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.job_type) q.set('job_type', params.job_type);
    if (params.entity_type) q.set('entity_type', params.entity_type);
    if (typeof params.limit === 'number') q.set('limit', String(params.limit));
    if (typeof params.offset === 'number') q.set('offset', String(params.offset));
    const qs = q.toString();
    const res = await authService.fetchWithAuth(`${API_URL}/email-service/jobs${qs ? `?${qs}` : ''}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить задачи рассылки');
    }
    const data = await res.json();
    return (Array.isArray(data) ? data : data?.items ?? []) as EmailJobListItem[];
  }

  async getJob(jobId: string): Promise<EmailJobResponse> {
    const res = await authService.fetchWithAuth(`${API_URL}/email-service/jobs/${encodeURIComponent(jobId)}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить задачу');
    }
    return res.json() as Promise<EmailJobResponse>;
  }

  async cancelJob(jobId: string): Promise<EmailJobActionResponse> {
    const res = await authService.fetchWithAuth(`${API_URL}/email-service/jobs/${encodeURIComponent(jobId)}/cancel`, {
      method: 'POST',
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось отменить задачу');
    }
    return res.json() as Promise<EmailJobActionResponse>;
  }

  async retryJob(jobId: string): Promise<EmailJobActionResponse> {
    const res = await authService.fetchWithAuth(`${API_URL}/email-service/jobs/${encodeURIComponent(jobId)}/retry`, {
      method: 'POST',
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось перезапустить задачу');
    }
    return res.json() as Promise<EmailJobActionResponse>;
  }

  async getJobEntity(
    jobId: string,
    entityType: string,
    entityId: number | string,
  ): Promise<EmailJobEntityResponse> {
    const res = await authService.fetchWithAuth(
      `${API_URL}/email-service/jobs/${encodeURIComponent(jobId)}/entities/${encodeURIComponent(entityType)}/${encodeURIComponent(String(entityId))}`
    );
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось получить статус письма по сущности');
    }
    return res.json() as Promise<EmailJobEntityResponse>;
  }

  // ===== Email Service Settings (global toggle) =====

  async getSettings(): Promise<EmailServiceSettings> {
    const res = await authService.fetchWithAuth(`${API_URL}/email-service/settings`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить настройки email');
    }
    return res.json() as Promise<EmailServiceSettings>;
  }

  async updateSettings(data: { enabled: boolean }): Promise<EmailServiceSettings> {
    const res = await authService.fetchWithAuth(`${API_URL}/email-service/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось обновить настройки email');
    }
    return res.json() as Promise<EmailServiceSettings>;
  }

  // ===== Email Account CRUD =====

  async createAccount(data: EmailAccountCreate): Promise<EmailAccount> {
    const res = await authService.fetchWithAuth(`${API_URL}/email-service/accounts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось создать email-аккаунт');
    }
    return res.json() as Promise<EmailAccount>;
  }

  async updateAccount(id: number, data: EmailAccountUpdate): Promise<EmailAccount> {
    const res = await authService.fetchWithAuth(`${API_URL}/email-service/accounts/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось обновить email-аккаунт');
    }
    return res.json() as Promise<EmailAccount>;
  }

  async deleteAccount(id: number): Promise<void> {
    const res = await authService.fetchWithAuth(`${API_URL}/email-service/accounts/${id}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось удалить email-аккаунт');
    }
  }

  async testAccount(id: number): Promise<EmailAccountTestResult> {
    const res = await authService.fetchWithAuth(`${API_URL}/email-service/accounts/${id}/test`, {
      method: 'POST',
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось проверить подключение');
    }
    return res.json() as Promise<EmailAccountTestResult>;
  }

  // ===== Entity Mappings =====

  async listMappings(accountId?: number): Promise<EmailEntityMapping[]> {
    const q = accountId != null ? `?account_id=${accountId}` : '';
    const res = await authService.fetchWithAuth(`${API_URL}/email-service/entity-mappings${q}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(formatApiError(err, 'Не удалось загрузить маппинги'));
    }
    const data = await res.json();
    return (Array.isArray(data) ? data : []) as EmailEntityMapping[];
  }

  async getMapping(serviceCode: string, accountId: number): Promise<EmailEntityMapping> {
    const res = await authService.fetchWithAuth(
      `${API_URL}/email-service/entity-mappings/${encodeURIComponent(serviceCode)}?account_id=${accountId}`
    );
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(formatApiError(err, 'Маппинг не найден'));
    }
    return res.json() as Promise<EmailEntityMapping>;
  }

  async createMapping(data: EmailEntityMappingCreate): Promise<EmailEntityMapping> {
    const res = await authService.fetchWithAuth(`${API_URL}/email-service/entity-mappings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(formatApiError(err, 'Не удалось создать маппинг'));
    }
    return res.json() as Promise<EmailEntityMapping>;
  }

  async updateMapping(serviceCode: string, accountId: number, data: EmailEntityMappingUpdate): Promise<EmailEntityMapping> {
    const res = await authService.fetchWithAuth(
      `${API_URL}/email-service/entity-mappings/${encodeURIComponent(serviceCode)}?account_id=${accountId}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    );
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(formatApiError(err, 'Не удалось обновить маппинг'));
    }
    return res.json() as Promise<EmailEntityMapping>;
  }

  async deleteMapping(serviceCode: string, accountId: number): Promise<void> {
    const res = await authService.fetchWithAuth(
      `${API_URL}/email-service/entity-mappings/${encodeURIComponent(serviceCode)}?account_id=${accountId}`,
      { method: 'DELETE' }
    );
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(formatApiError(err, 'Не удалось удалить маппинг'));
    }
  }

  // ===== Bulk email =====

  async sendBulkEntity(payload: BulkEmailPayload, files: File[] = []): Promise<BulkEmailResponse> {
    const form = new FormData();
    form.append('request', JSON.stringify(payload));
    files.forEach((file) => form.append('files', file, file.name));

    const res = await authService.fetchWithAuthForm(`${API_URL}/email-service/send/bulk-entity`, {
      method: 'POST',
      body: form,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || err?.error || 'Не удалось отправить рассылку');
    }
    return res.json();
  }
}

export default new EmailService();
